import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllSessions } from '../../actions/sessionActions';
import { APPEND_BACKEND_EVENTS } from '../../actions/types';
import configData from '../../configData.json';
import { HYDRATE_SESSION_TOOL_BUNDLES } from '../../actions/types';

// Read helpers from localStorage
function lsGet(key, fallback) {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
}
function lsSet(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }

// const BACKUP_INTERVAL = 15 * 60 * 1000; // 15 minutes
const BACKUP_INTERVAL = 5 * 60 * 1000; // 5 minutes test
const DATA_REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes
const PENDING_RETRY_INTERVAL = 60 * 1000;   // 1 minute
const MAX_SYNC_ATTEMPTS = 3;
const LOCK_TTL_MS = 2 * 60 * 1000; // 2 minutes per-session lock to avoid duplicate posts
// export const AUTOSAVE_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes between autosave flushes
export const AUTOSAVE_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes between autosave flushes test
const AUTOSAVE_THROTTLE_MS = AUTOSAVE_INTERVAL_MS - 60 * 1000; // prevent double autosaves within 9 minutes
const AUTOSAVE_META_KEY = 'autosaveMeta';
// Queue hygiene constants
const MAX_PENDING_QUEUE = 50; // cap total queued entries (most recent kept)
const INFER_BACKUP_ENDED_AT_IF_NOT_CURRENT = true; // infer safe endedAt for backups when session is no longer current
// Simple guard to avoid overlapping drains causing rate limits
let isPendingDrainRunning = false;
let autosaveInFlight = false;

// Simple per-session lock using localStorage to avoid concurrent drains creating duplicates
function acquireSessionLock(sessionId) {
  try {
    if (!sessionId) return false;
    const locks = lsGet('pendingSyncLocks', {});
    const now = Date.now();
    const lock = locks[sessionId];
    if (lock && (now - (lock.timestamp || 0) < LOCK_TTL_MS)) {
      return false; // another drain is handling this session
    }
    locks[sessionId] = { timestamp: now };
    lsSet('pendingSyncLocks', locks);
    return true;
  } catch { return true; }
}

function releaseSessionLock(sessionId) {
  try {
    if (!sessionId) return;
    const locks = lsGet('pendingSyncLocks', {});
    if (locks[sessionId]) {
      delete locks[sessionId];
      lsSet('pendingSyncLocks', locks);
    }
  } catch {}
}

export default function BackendSyncAgent() {
  const dispatch = useDispatch();
  const auth = useSelector(s => s.auth);
  const userState = useSelector(s => s.user);

  const intervalsRef = useRef([]);

  useEffect(() => {
    // Clean existing timers (hot reload safety)
    intervalsRef.current.forEach(id => clearInterval(id));
    intervalsRef.current = [];

  // On startup: sync any pending sessions from previous crashes/failed syncs
  syncPendingSessionsOnStartup();

    // Periodic backup of current session (every 15 minutes for crash safety)
    const backupTimer = setInterval(() => {
      try {
        const currentSession = lsGet('currentUserSession', null);
        const currentTool = lsGet('currentTool', null);
        
        if (currentSession && !currentSession.endedAt) {
          console.log('💾 [BackendSync] Creating backup snapshot');
          createBackupSnapshot(currentSession, currentTool);
        }
      } catch (e) {
        console.warn('Backup snapshot failed:', e);
      }
    }, BACKUP_INTERVAL);
    intervalsRef.current.push(backupTimer);

    // Periodic refresh of backend data for dashboard (every 2 minutes)
    const refreshTimer = setInterval(() => {
      try {
        dispatch(getAllSessions());
      } catch (e) {
        console.warn('Data refresh failed:', e);
      }
    }, DATA_REFRESH_INTERVAL);
    intervalsRef.current.push(refreshTimer);

    // Drain pending queue when connection comes back
    const onOnline = () => {
      setTimeout(() => {
        try { syncPendingSessionsOnStartup(); } catch (e) {}
      }, 2000);
    };
    window.addEventListener('online', onOnline);

    // Periodic retry for pending queue if online and token available
    const pendingRetryTimer = setInterval(() => {
      try {
        if (navigator.onLine) {
          const tokenPresent = !!getAuthToken();
          if (tokenPresent) syncPendingSessionsOnStartup();
        }
      } catch {}
    }, PENDING_RETRY_INTERVAL);
    intervalsRef.current.push(pendingRetryTimer);

    return () => {
  intervalsRef.current.forEach(id => clearInterval(id));
      intervalsRef.current = [];
  try { window.removeEventListener('online', onOnline); } catch {}
    };
  }, [dispatch, auth, userState]);

  return null;
}

// Expand an aggregate wrapper document into detailed per-tool events
function normalizeAggregateToDetail(aggregateDoc) {
  if (!aggregateDoc) return [];
  const out = [];
  const email = aggregateDoc.email;
  const sid = aggregateDoc.sessionId;
  const ts = aggregateDoc.timestamp || new Date().toISOString();
  const push = (arr, type) => {
    (arr || []).forEach(t => {
      const tool = typeof t === 'string' ? t : (t?.tool || t?.name);
      if (!tool) return;
      out.push({ details: { tool }, sessionId: sid, sessionType: type, timestamp: ts, email });
    });
  };
  push(aggregateDoc.personalTools, 'personal_space');
  push(aggregateDoc.projectTools, 'project_space');
  push(aggregateDoc.managerTools, 'manager_space');
  return out;
}

// Sync any sessions that failed to sync in previous sessions
export async function syncPendingSessionsOnStartup() {
  try {
    if (isPendingDrainRunning) {
      console.log('🔄 [BackendSync] Pending sync skipped: drain already running');
      return;
    }
    isPendingDrainRunning = true;
    // Skip if offline or no auth token yet
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      console.log('🔄 [BackendSync] Pending sync skipped: offline');
      return;
    }
    const tokenPresent = !!getAuthToken();
    if (!tokenPresent) {
      console.log('🔄 [BackendSync] Pending sync skipped: no auth token');
      return;
    }
  // First, clean and compact the queue (dedupe, infer endedAt, cap size)
  const pendingSessions = cleanAndCompactPendingQueue();
  console.log(`🔄 [BackendSync] Found ${pendingSessions.length} pending sessions to sync after cleanup`);
    
    for (const pendingData of pendingSessions) {
      // If this is a backup snapshot without endedAt, skip until session is closed
      if (!pendingData?.session?.endedAt) {
        if (pendingData?.source === 'logout') {
          // Harden logout snapshot by ensuring endedAt exists now
          pendingData.session.endedAt = pendingData.session.endedAt || pendingData.backupTime || new Date().toISOString();
          pendingData.session.endReason = pendingData.session.endReason || 'logout';
          // Persist the fix-up
          const updated = lsGet('pendingSessions', []).map(p => p.sessionId === pendingData.sessionId ? pendingData : p);
          lsSet('pendingSessions', updated);
        } else if (pendingData?.source === 'backup') {
          const backupEndedAt = pendingData.backupTime || new Date().toISOString();
          pendingData.session.endedAt = backupEndedAt;
          pendingData.session.endReason = pendingData.session.endReason || 'autosave_backup';
          const updated = lsGet('pendingSessions', []).map(p => p.sessionId === pendingData.sessionId ? pendingData : p);
          lsSet('pendingSessions', updated);
        } else {
          console.log(`⏭️ [BackendSync] Skipping backup snapshot without endedAt (session ${pendingData.sessionId}); waiting for session closure`);
          continue;
        }
      }

      // Acquire a per-session lock to avoid duplicate concurrent syncs
      if (!acquireSessionLock(pendingData.sessionId)) {
        console.log(`🔒 [BackendSync] Session ${pendingData.sessionId} is already being synced; skipping`);
        continue;
      }

      const success = await attemptSessionSync(pendingData);
      if (success) {
        // Remove successfully synced session from pending (all entries for this session)
        const updated = lsGet('pendingSessions', []).filter(p => p.sessionId !== pendingData.sessionId);
        lsSet('pendingSessions', updated);
        console.log(`✅ [BackendSync] Successfully synced pending session: ${pendingData.sessionId}`);
      } else {
        // Increment attempt count only when online and token present (i.e., a real failure)
        if (navigator.onLine && tokenPresent) {
          pendingData.syncAttempts = (pendingData.syncAttempts || 0) + 1;
        }
        if ((pendingData.syncAttempts || 0) >= MAX_SYNC_ATTEMPTS) {
          console.warn(`⏳ [BackendSync] Max attempts reached for now; will retry later: ${pendingData.sessionId}`);
          const updated = lsGet('pendingSessions', []).map(p => 
            p.sessionId === pendingData.sessionId ? pendingData : p
          );
          lsSet('pendingSessions', updated);
        } else {
          const updated = lsGet('pendingSessions', []).map(p => 
            p.sessionId === pendingData.sessionId ? pendingData : p
          );
          lsSet('pendingSessions', updated);
          console.warn(`❌ [BackendSync] Failed to sync session (attempt ${pendingData.syncAttempts}/${MAX_SYNC_ATTEMPTS}): ${pendingData.sessionId}`);
        }
      }
      // Always release lock after an attempt
      releaseSessionLock(pendingData.sessionId);
    }
  } catch (e) {
    console.warn('Failed to sync pending sessions:', e);
  } finally {
    isPendingDrainRunning = false;
  }
}

// Create backup snapshot for crash recovery
function createBackupSnapshot(currentSession, currentTool) {
  try {
    const pendingSessions = lsGet('pendingSessions', []);
    const sessionId = currentSession.sessionId;
    
    // Remove any existing backup for this session
    const filteredPending = pendingSessions.filter(p => p.sessionId !== sessionId);
    
    // Add new backup
    const backupEndedAt = new Date().toISOString();
    const backupData = {
      sessionId: sessionId,
      session: {
        ...currentSession,
        endedAt: currentSession.endedAt || backupEndedAt,
        endReason: currentSession.endReason || 'autosave_backup'
      },
      tools: currentTool ? { ...currentTool } : null,
      backupTime: backupEndedAt,
      syncAttempts: 0,
      source: 'backup'
    };
    
    filteredPending.push(backupData);
    // Cap the queue size to avoid unbounded growth
    const capped = capQueue(filteredPending);
    lsSet('pendingSessions', capped);
    
    console.log(`💾 [BackendSync] Backup created for session: ${sessionId}`);
  } catch (e) {
    console.warn('Failed to create backup snapshot:', e);
  }
}

// Called when user logs out or session ends - sync current session to backend
export async function syncSessionOnLogout(sessionData = null) {
  try {
    // Use provided session data or get from localStorage as fallback
    let currentSession = sessionData;
    if (!currentSession) {
      currentSession = lsGet('currentUserSession', null);
    }
    
    const currentTool = lsGet('currentTool', null);
    
    console.log('🚪 [BackendSync] syncSessionOnLogout called, sessionId:', currentSession?.sessionId);
    
    if (!currentSession) {
      console.error('🚪 [BackendSync] CRITICAL: No session data to sync on logout');
      return false; // Return false instead of true to indicate failure
    }

    // Verify auth token exists before attempting sync
    const authToken = getAuthToken();
    if (!authToken) {
      console.error('🚪 [BackendSync] CRITICAL: No auth token available for sync');
      return false;
    }

    // Ensure session has endedAt
    if (!currentSession.endedAt) {
      currentSession.endedAt = new Date().toISOString();
      currentSession.endReason = 'logout';
      console.log('🚪 [BackendSync] Added endedAt to session:', currentSession.endedAt);
    }

    console.log('🚪 [BackendSync] Syncing session on logout:', {
      sessionId: currentSession.sessionId,
      userId: currentSession.userId,
      startedAt: currentSession.startedAt,
      endedAt: currentSession.endedAt,
      heartbeats: currentSession.heartbeats?.length || 0,
      hasTools: !!currentTool,
      authToken: authToken ? 'PRESENT' : 'MISSING'
    });

    const sessionDataToSync = {
      sessionId: currentSession.sessionId,
      session: currentSession,
      tools: currentTool,
      syncAttempts: 0,
      source: 'logout'
    };

    const success = await attemptSessionSync(sessionDataToSync);
    
    if (!success) {
      console.error('❌ [BackendSync] CRITICAL: Logout sync failed completely');
      // Add to pending for retry later
      const pendingSessions = lsGet('pendingSessions', []);
      const filtered = pendingSessions.filter(p => p.sessionId !== currentSession.sessionId);
      filtered.push(sessionDataToSync);
      lsSet('pendingSessions', filtered);
      console.warn('💾 [BackendSync] Session added to pending queue for retry');
    } else {
      console.log('✅ [BackendSync] Session successfully synced to backend on logout');
  // Clear currentTool to avoid duplicate resend on next app start
  try { localStorage.removeItem('currentTool'); } catch {}
      
      // Trigger immediate Redux refresh to show the new session
      try {
        const store = require('../../store').default;
        const { getAllSessions } = require('../../actions/sessionActions');
        console.log('🔄 [BackendSync] Triggering immediate Redux refresh after sync');
        store.dispatch(getAllSessions());
        console.log('🔄 [BackendSync] Redux refresh dispatched successfully');
      } catch (e) {
        console.log('⚠️ [BackendSync] Could not trigger immediate refresh:', e.message);
      }
    }

    return success;
  } catch (e) {
    console.error('💥 [BackendSync] CRITICAL ERROR in syncSessionOnLogout:', e);
    return false;
  }
}

// Autosave the ongoing session without closing it (runs on a timer)
export async function autosaveCurrentSession(trigger = 'interval') {
  if (autosaveInFlight) {
    console.log('⏳ [BackendSync] Autosave skipped: another autosave in flight');
    return false;
  }
  autosaveInFlight = true;
  try {
    const currentSession = lsGet('currentUserSession', null);
    if (!currentSession || currentSession.endedAt) {
      console.log('ℹ️ [BackendSync] Autosave skipped: no active session');
      return false;
    }

    const autosaveMeta = lsGet(AUTOSAVE_META_KEY, {});
    const now = Date.now();
    if (
      autosaveMeta?.lastAt &&
      autosaveMeta.lastSessionId === currentSession.sessionId &&
      now - autosaveMeta.lastAt < AUTOSAVE_THROTTLE_MS
    ) {
      console.log('⏭️ [BackendSync] Autosave skipped: throttle window active');
      return false;
    }

    const snapshotSession = JSON.parse(JSON.stringify(currentSession));
    snapshotSession.endedAt = new Date().toISOString();
    snapshotSession.endReason = trigger === 'manual' ? 'autosave_manual' : 'autosave';
    snapshotSession.autosave = true;

    const currentTool = lsGet('currentTool', null);
    const payload = {
      sessionId: snapshotSession.sessionId,
      session: snapshotSession,
      tools: currentTool,
      syncAttempts: 0,
      source: 'autosave'
    };

    const isOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
    const authToken = getAuthToken();
    if (!isOnline || !authToken) {
      console.warn('🌐 [BackendSync] Autosave queued (offline or missing token)');
      const pendingOffline = lsGet('pendingSessions', []).filter(p => !(p.sessionId === snapshotSession.sessionId && p.source === 'autosave'));
      pendingOffline.push(payload);
      lsSet('pendingSessions', capQueue(pendingOffline));
      lsSet(AUTOSAVE_META_KEY, {
        lastAt: now,
        lastSessionId: snapshotSession.sessionId,
        lastResult: 'queued_offline',
        lastTrigger: trigger,
        lastDuration: snapshotSession.durationMs || 0
      });
      return false;
    }

    const success = await attemptSessionSync(payload);

    if (success) {
      lsSet(AUTOSAVE_META_KEY, {
        lastAt: now,
        lastSessionId: snapshotSession.sessionId
      });
      console.log('✅ [BackendSync] Autosave synced successfully');
    } else {
      console.warn('⚠️ [BackendSync] Autosave sync failed, queueing for retry');
      const pending = lsGet('pendingSessions', []).filter(p => !(p.sessionId === snapshotSession.sessionId && p.source === 'autosave'));
      pending.push(payload);
      lsSet('pendingSessions', capQueue(pending));
    }

    return success;
  } catch (error) {
    console.error('💥 [BackendSync] Autosave encountered an error:', error);
    return false;
  } finally {
    autosaveInFlight = false;
  }
}

// Attempt to sync a session + tools to backend
async function attemptSessionSync(sessionData) {
  try {
    const { session, tools } = sessionData;
    
    // Ensure session has endedAt (required for backend)
    if (!session.endedAt) {
      console.error('❌ [BackendSync] Session missing endedAt, cannot sync:', session.sessionId);
      return false;
    }

    // Verify required session fields
    if (!session.sessionId || !session.userId || !session.startedAt) {
      console.error('❌ [BackendSync] Session missing required fields:', {
        sessionId: !!session.sessionId,
        userId: !!session.userId,
        startedAt: !!session.startedAt
      });
      return false;
    }

    // Determine if we should CREATE or UPDATE based on backendId presence
    const hasBackendId = !!session.backendId;
    const isUpdate = hasBackendId;
    const isCreate = !hasBackendId;

    // Build payload based on create vs update
    const sessionPayload = {
      createSession: isCreate,  // true = create new, false = update existing
      backendId: session.backendId || undefined,  // MongoDB _id for updates
      session: {
        sessionId: session.sessionId,
        email: session.userId || session.email,
        startedAt: session.startedAt,
        endedAt: session.endedAt,
        heartbeats: session.heartbeats || [],
        activity_duration: session.activity_duration || 0,
        inactivity_duration: session.inactivity_duration || 0,
        sessionDurationReached: session.sessionDurationReached || false,
        endReason: session.endReason || undefined,
        autosave: session.autosave || undefined
      }
    };

    console.log('📤 [BackendSync] Attempting to sync session:', {
      operation: isUpdate ? 'UPDATE' : 'CREATE',
      sessionId: session.sessionId,
      backendId: session.backendId || 'none',
      email: session.userId || session.email,
      startedAt: session.startedAt,
      endedAt: session.endedAt,
      heartbeatsCount: session.heartbeats?.length || 0,
      endReason: session.endReason,
      isAutosave: !!session.autosave,
      payloadSize: JSON.stringify(sessionPayload).length,
      serverUrl: `${configData.SERVER_URL}createSessionOrEvent`
    });

    const authToken = getAuthToken();
    if (!authToken) {
      console.error('❌ [BackendSync] No auth token available for API request');
      return false;
    }

    // DEBUG: Log exact payload being sent to backend
    console.log('🚀 [DEBUG] EXACT PAYLOAD SENT TO BACKEND:', JSON.stringify(sessionPayload, null, 2));

    // Send to backend (create or update)
    const response = await fetch(`${configData.SERVER_URL}createSessionOrEvent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': authToken
      },
      body: JSON.stringify(sessionPayload)
    });

    console.log('📤 [BackendSync] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('📤 [BackendSync] Response error:', {
        status: response.status,
        statusText: response.statusText,
        errorText: errorText
      });
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    const responseData = await response.json();
    // Get backendId from response (CREATE) or use existing one (UPDATE)
    let backendId = responseData?.session?._id || responseData?.session?.id || session.backendId || null;
    
    console.log(`✅ [BackendSync] Session ${isUpdate ? 'updated' : 'created'}:`, {
      sessionId: session.sessionId,
      backendId: backendId,
      timestamp: new Date().toISOString()
    });

    // If this was a CREATE, store the backendId for future updates
    if (isCreate && backendId) {
      // Update the session in localStorage with backendId
      const currentSession = lsGet('currentUserSession', null);
      if (currentSession && currentSession.sessionId === session.sessionId) {
        currentSession.backendId = backendId;
        lsSet('currentUserSession', currentSession);
        console.log('✅ [BackendSync] Stored backendId in current session:', backendId);
      }
      
      // Also track in syncedSessionIds for reference
      const syncedSessions = lsGet('syncedSessionIds', {});
      syncedSessions[session.sessionId] = {
        syncedAt: new Date().toISOString(),
        backendId: backendId
      };
      lsSet('syncedSessionIds', syncedSessions);
    }

    // 2) Transform tools -> events for backend and send in a second call (if any)
  let eventsPayload = null;
  let aggregatePayload = null;
  if (tools) {
      const email = session.userId || session.email || tools.userEmail;
      const events = [];
      const pushEvents = (arr, sessionType) => {
        try {
          (arr || []).forEach(t => {
            if (!t) return;
            const timestamp = t.timestamp || new Date().toISOString();
            const toolName = typeof t === 'string' ? t : (t.tool || t.name || 'unknown');
            events.push({
              details: { tool: toolName },
              sessionId: backendId || session.sessionId,
              sessionType,
              timestamp,
              email
            });
          });
        } catch {}
  }

      pushEvents(tools.projectTools, 'project_space');
      pushEvents(tools.personalTools, 'personal_space');
      pushEvents(tools.managerTools, 'manager_space');

      if (events.length > 0 || (tools.personalTools?.length || tools.projectTools?.length || tools.managerTools?.length)) {
        // Build detailed events payload as fallback/canonical compatible with existing schema
        if (events.length > 0) {
          eventsPayload = {
            createEvents: true,
            events
          };
        }
        // Build aggregate payload as the canonical bundle with full tool objects
        aggregatePayload = {
          createEvents: true,
          events: [
            {
              details: { tool: 'aggregate' },
              sessionId: backendId || session.sessionId,
              // Use a valid enum value for sessionType to avoid schema validation errors.
              // The backend detects aggregates via details.tool === 'aggregate'.
              sessionType: 'personal_space',
              timestamp: new Date().toISOString(),
              email,
              // Store only tool names as strings (backend expects names only)
              personalTools: (tools.personalTools || []).map(t => (typeof t === 'string' ? t : (t?.tool || t?.name || ''))).filter(Boolean),
              projectTools: (tools.projectTools || []).map(t => (typeof t === 'string' ? t : (t?.tool || t?.name || ''))).filter(Boolean),
              managerTools: (tools.managerTools || []).map(t => (typeof t === 'string' ? t : (t?.tool || t?.name || ''))).filter(Boolean)
            }
          ]
        };
  const pc = tools.projectTools?.length || 0;
  const pe = tools.personalTools?.length || 0;
  const mc = tools.managerTools?.length || 0;
  console.log(`🧮 [BackendSync] Built detailed events from tools: project=${pc} personal=${pe} manager=${mc} total=${events.length}`);
  const sample = events.slice(0, 5).map(e => `${e.sessionType}:${e.details?.tool}`);
  if (sample.length) console.log('🧮 [BackendSync] Sample tools:', sample.join(', '));
      }
    }

    // Prefer aggregate (bundle) so backend stores arrays we can fetch later
    const preferAggregate = !!aggregatePayload;

    if ((eventsPayload || aggregatePayload) && backendId) {
      const sendPayload = async (payload, label) => {
        console.log('📤 [BackendSync] Sending events (step 2/2):', {
          eventsCount: payload?.events?.length || 0,
          usesBackendId: !!(backendId),
          payloadSize: JSON.stringify(payload).length,
          strategy: label
        });
        const resp = await fetch(`${configData.SERVER_URL}createSessionOrEvent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': authToken
          },
          body: JSON.stringify(payload)
        });
        if (!resp.ok) {
          const errorText = await resp.text();
          console.error('📤 [BackendSync] Events response error:', {
            status: resp.status,
            statusText: resp.statusText,
            errorText
          });
          throw new Error(`HTTP ${resp.status}: ${resp.statusText} - ${errorText}`);
        }
      };

      let appendedPayload = null;
      try {
        if (preferAggregate) {
          await sendPayload(aggregatePayload, 'aggregate');
          appendedPayload = normalizeAggregateToDetail(aggregatePayload.events[0]);
        } else if (eventsPayload) {
          await sendPayload(eventsPayload, 'detail');
          appendedPayload = eventsPayload.events;
        }
        console.log('✅ [BackendSync] Events saved successfully');
      } catch (e) {
        // Fallback to detailed events if aggregate fails (e.g., schema enum restrictions)
        if (preferAggregate && eventsPayload) {
          console.warn('⚠️ [BackendSync] Aggregate failed, retrying with detailed events...');
          await sendPayload(eventsPayload, 'detail-fallback');
          appendedPayload = eventsPayload.events;
          console.log('✅ [BackendSync] Detailed events saved on fallback');
        } else {
          throw e;
        }
      }

      // Optimistically append these events to Redux immediately
      try {
        const store = require('../../store').default;
        const payloadToAppend = appendedPayload || [];
        store.dispatch({ type: APPEND_BACKEND_EVENTS, payload: payloadToAppend });
        // Also upsert a bundle into local cache and hydrate so refresh won’t drop it
        const { HYDRATE_SESSION_TOOL_BUNDLES } = require('../../actions/types');
      const buildBundleFromEvents = (evts) => {
          const aggregatedMap = new Map();
          const ensureRec = (sid, seedEvt) => {
            if (!aggregatedMap.has(sid)) {
              aggregatedMap.set(sid, {
                sessionId: sid,
                datetime: seedEvt?.timestamp || new Date().toISOString(),
                managerTools: [], personalTools: [], projectTools: [],
                userEmail: seedEvt?.email || ''
              });
            }
            return aggregatedMap.get(sid);
          };
          const hasTool = (arr, tool) => arr.some(t => (t.tool || t) === tool);
      evts.forEach(evt => {
            const sid = evt.sessionId || 'unknown';
            const rec = ensureRec(sid, evt);
        const tool = (evt?.details?.tool || evt.tool || '').toString();
            const type = (evt.sessionType || '').toLowerCase();
        if (!tool) return;
        // Store bundles as strings
        if (type === 'manager_space') { if (!hasTool(rec.managerTools, tool)) rec.managerTools.push(tool); }
        else if (type === 'personal_space') { if (!hasTool(rec.personalTools, tool)) rec.personalTools.push(tool); }
        else if (type === 'project_space') { if (!hasTool(rec.projectTools, tool)) rec.projectTools.push(tool); }
            if (evt.timestamp && rec.datetime && new Date(evt.timestamp) < new Date(rec.datetime)) rec.datetime = evt.timestamp;
            if (!rec.userEmail && evt.email) rec.userEmail = evt.email;
          });
          return Array.from(aggregatedMap.values());
        };
        const bundles = buildBundleFromEvents(payloadToAppend);
        if (bundles.length) {
          const cached = JSON.parse(localStorage.getItem('sessionToolBundlesCache') || '[]');
          const byId = new Map(cached.map(b => [b.sessionId, b]));
          const union = (oldArr = [], newArr = []) => {
            const seen = new Set();
            const out = [];
            [...oldArr, ...newArr].forEach(x => { const name = x?.tool || x; if (!seen.has(name)) { seen.add(name); out.push(x); } });
            return out;
          };
          bundles.forEach(b => {
            const prev = byId.get(b.sessionId);
            if (!prev) byId.set(b.sessionId, b);
            else byId.set(b.sessionId, {
              sessionId: b.sessionId,
              datetime: new Date(b.datetime) < new Date(prev.datetime || b.datetime) ? b.datetime : (prev.datetime || b.datetime),
              userEmail: b.userEmail || prev.userEmail || '',
              managerTools: union(prev.managerTools, b.managerTools),
              personalTools: union(prev.personalTools, b.personalTools),
              projectTools: union(prev.projectTools, b.projectTools)
            });
          });
          localStorage.setItem('sessionToolBundlesCache', JSON.stringify(Array.from(byId.values())));
          store.dispatch({ type: HYDRATE_SESSION_TOOL_BUNDLES, payload: Array.from(byId.values()) });
        }
      } catch (e) {
        console.log('⚠️ [BackendSync] Could not append events optimistically:', e?.message);
      }
    } else if (eventsPayload && !backendId) {
      console.warn('⚠️ [BackendSync] Events present but no backendId returned; skipping events to avoid backend errors');
    } else {
      console.log('ℹ️ [BackendSync] No events to send for this session');
    }

    return true;
  } catch (error) {
    console.error('❌ [BackendSync] Sync failed with error:', {
      sessionId: sessionData.session?.sessionId,
      errorMessage: error.message,
      errorStack: error.stack,
      timestamp: new Date().toISOString()
    });
    return false;
  }
}

// Debug function to manually test session sync (add to window for console access)
export function debugTestSync() {
  console.log('🔧 [DEBUG] Manual sync test initiated');
  
  const currentSession = lsGet('currentUserSession', null);
  console.log('🔧 [DEBUG] Current session:', currentSession);
  
  if (currentSession) {
    // Create a test session with endedAt
    const testSession = {
      ...currentSession,
      endedAt: new Date().toISOString(),
      endReason: 'debug_test'
    };
    
    return syncSessionOnLogout(testSession);
  } else {
    console.log('🔧 [DEBUG] No current session to test');
    return Promise.resolve(false);
  }
}

// Make debug function available in browser console
if (typeof window !== 'undefined') {
  window.debugTestSync = debugTestSync;
}

// Debug function to check current state
export function debugCurrentState() {
  console.log('🔧 [DEBUG] Current state check:');
  console.log('🔧 Current Session:', lsGet('currentUserSession', null));
  console.log('🔧 Current Tool:', lsGet('currentTool', null));
  console.log('🔧 Pending Sessions:', lsGet('pendingSessions', []));
  console.log('🔧 User Sessions:', lsGet('userSessions', []));
  console.log('🔧 Auth Token:', getAuthToken() ? 'PRESENT' : 'MISSING');
}

if (typeof window !== 'undefined') {
  window.debugCurrentState = debugCurrentState;
}

// Get auth token for API requests
export function getAuthToken() {
  try {
    // Check multiple possible token locations
    const authState = JSON.parse(localStorage.getItem('authState') || '{}');
    let token = authState.token;
    
    // Fallback: check for token directly in localStorage
    if (!token) {
      token = localStorage.getItem('token');
    }
    
    // Fallback: check for auth object with token
    if (!token) {
      const authObj = JSON.parse(localStorage.getItem('auth') || '{}');
      token = authObj.token;
    }
    
    console.log('🔑 [BackendSync] Auth token check:', {
      authStateExists: !!authState,
      tokenExists: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'NOT FOUND',
      allLocalStorageKeys: Object.keys(localStorage).filter(k => k.toLowerCase().includes('auth') || k.toLowerCase().includes('token'))
    });
    
    return token;
  } catch (error) {
    console.error('🔑 [BackendSync] Failed to get auth token:', error);
    return null;
  }
}

// ---- Queue hygiene helpers ----
function capQueue(queue) {
  try {
    if (!Array.isArray(queue)) return [];
    if (queue.length <= MAX_PENDING_QUEUE) return queue;
    // Keep most recent by backupTime or by presence of endedAt/source priority
    const sorted = [...queue].sort((a, b) => {
      const aScore = (a?.source === 'logout' ? 2 : 0) + (a?.session?.endedAt ? 1 : 0);
      const bScore = (b?.source === 'logout' ? 2 : 0) + (b?.session?.endedAt ? 1 : 0);
      if (aScore !== bScore) return bScore - aScore;
      const at = new Date(a?.backupTime || 0).getTime();
      const bt = new Date(b?.backupTime || 0).getTime();
      return bt - at;
    });
    return sorted.slice(0, MAX_PENDING_QUEUE);
  } catch {
    return queue;
  }
}

function cleanAndCompactPendingQueue() {
  try {
    const raw = lsGet('pendingSessions', []);
    if (!Array.isArray(raw) || raw.length === 0) return [];

    const current = lsGet('currentUserSession', null);
    const currentId = current?.sessionId;
    const archived = lsGet('userSessions', []);
    const archivedById = new Map((archived || []).map(s => [s.sessionId, s]));

    // Group by sessionId and keep best candidate per group
    const groups = new Map();
    for (const p of raw) {
      if (!p?.sessionId) continue;
      const existing = groups.get(p.sessionId);
      const pick = (a, b) => {
        if (!a) return b;
        if (!b) return a;
        // Prefer logout > autosave > backup; prefer entry with endedAt; then most recent backupTime
        const sourceScore = (src) => {
          if (src === 'logout') return 3;
          if (src === 'autosave') return 2;
          return 0;
        };
        const aScore = sourceScore(a?.source) + (a?.session?.endedAt ? 1 : 0);
        const bScore = sourceScore(b?.source) + (b?.session?.endedAt ? 1 : 0);
        if (aScore !== bScore) return aScore > bScore ? a : b;
        const at = new Date(a?.backupTime || 0).getTime();
        const bt = new Date(b?.backupTime || 0).getTime();
        return at >= bt ? a : b;
      };
      groups.set(p.sessionId, pick(existing, p));
    }

    // Post-process: infer endedAt for backups that are no longer current or have archived info
    const cleaned = [];
    for (const p of groups.values()) {
      const sess = p.session || {};
      if (!sess.endedAt && p.source === 'backup') {
        const archivedRec = archivedById.get(p.sessionId);
        if (archivedRec?.endedAt) {
          sess.endedAt = archivedRec.endedAt;
          sess.endReason = archivedRec.endReason || sess.endReason || 'archived_restore';
  } else if (INFER_BACKUP_ENDED_AT_IF_NOT_CURRENT && (!currentId || currentId !== p.sessionId)) {
          // infer a conservative endedAt using backupTime (session likely closed elsewhere)
          if (p.backupTime) {
            sess.endedAt = p.backupTime;
            sess.endReason = sess.endReason || 'inferred_backup';
          }
        }
      }
      cleaned.push({ ...p, session: sess });
    }

    const finalQueue = capQueue(cleaned);
    // Persist back only if changed in size/content
    try {
      const before = JSON.stringify(raw);
      const after = JSON.stringify(finalQueue);
      if (before !== after) lsSet('pendingSessions', finalQueue);
    } catch {}
    return finalQueue;
  } catch {
    return lsGet('pendingSessions', []);
  }
}

// Debug utility for testing session flow
if (typeof window !== 'undefined') {
  window.debugSessionFlow = function() {
    console.log('\n🔍 ===== SESSION DEBUG INFO =====');
    
    const currentSession = lsGet('currentUserSession', null);
    const pendingSessions = lsGet('pendingSessions', []);
    const syncedSessions = lsGet('syncedSessionIds', {});
    const autosaveMeta = lsGet(AUTOSAVE_META_KEY, {});
    
    console.log('\n📊 CURRENT SESSION:');
    if (currentSession) {
      console.log({
        sessionId: currentSession.sessionId,
        backendId: currentSession.backendId || '❌ NOT SET',
        userId: currentSession.userId,
        startedAt: currentSession.startedAt,
        endedAt: currentSession.endedAt || 'Still active',
        heartbeatsCount: currentSession.heartbeats?.length || 0,
        activity_duration: currentSession.activity_duration,
        inactivity_duration: currentSession.inactivity_duration
      });
    } else {
      console.log('❌ No current session');
    }
    
    console.log('\n📦 PENDING SESSIONS:', pendingSessions.length);
    pendingSessions.forEach((p, i) => {
      console.log(`  ${i + 1}.`, {
        sessionId: p.sessionId,
        backendId: p.session?.backendId || 'none',
        source: p.source,
        syncAttempts: p.syncAttempts || 0,
        endedAt: p.session?.endedAt
      });
    });
    
    console.log('\n✅ SYNCED SESSIONS:', Object.keys(syncedSessions).length);
    Object.entries(syncedSessions).forEach(([sessionId, info]) => {
      console.log(`  - ${sessionId}:`, {
        backendId: info.backendId,
        syncedAt: info.syncedAt
      });
    });
    
    console.log('\n⏰ AUTOSAVE META:');
    console.log({
      lastAt: autosaveMeta.lastAt ? new Date(autosaveMeta.lastAt).toLocaleString() : 'never',
      lastSessionId: autosaveMeta.lastSessionId || 'none',
      timeSinceLastAutosave: autosaveMeta.lastAt ? `${Math.floor((Date.now() - autosaveMeta.lastAt) / 1000)}s ago` : 'N/A'
    });
    
    console.log('\n🔍 ===== END DEBUG INFO =====\n');
    
    return {
      currentSession,
      pendingSessions,
      syncedSessions,
      autosaveMeta,
      hasBackendId: !!currentSession?.backendId
    };
  };
  
  console.log('💡 Debug utility loaded! Type window.debugSessionFlow() in console to inspect session state');
}
